import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { units as mockUnits, galleryImages } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import TresLogo from '../components/tres/TresLogo';
import InfoIcon from '../components/tres/InfoIcon';
import SeoHead from '../components/SeoHead';
import { localizedPath } from '../config/seo';
import { loadAnalytics, loadRecaptcha, trackEvent } from '../lib/analytics';
import './TresImmo.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const RECAPTCHA_SITE_KEY = '6Ldla64tAAAAABIw9Redeab0VIFSBI4AbNUB4LXE';

const WhatsAppIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12 0C5.373 0 0 5.373 0 12c0 2.124.554 4.118 1.528 5.845L.057 23.776a.5.5 0 00.636.599l6.097-1.995A11.956 11.956 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.9 0-3.683-.516-5.21-1.415l-.374-.22-3.876 1.269 1.22-3.77-.238-.38A10 10 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" />
  </svg>
);

// Count-up animation on scroll-into-view (for card-numbers "11" / "1200")
const AnimatedNumber = ({ value, duration = 1800 }) => {
  const ref = useRef(null);
  const [display, setDisplay] = useState(value);
  const started = useRef(false);

  useEffect(() => {
    const target = parseInt(String(value).replace(/[^0-9]/g, ''), 10);
    // Non-numeric values (e.g. ∞) render as-is without animation
    if (!Number.isFinite(target)) { setDisplay(value); return; }
    setDisplay('0');
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting && !started.current) {
          started.current = true;
          const start = performance.now();
          const tick = (now) => {
            const p = Math.min(1, (now - start) / duration);
            // easeOutCubic
            const eased = 1 - Math.pow(1 - p, 3);
            const cur = Math.round(target * eased);
            setDisplay(String(cur));
            if (p < 1) requestAnimationFrame(tick);
            else setDisplay(String(target));
          };
          requestAnimationFrame(tick);
          io.disconnect();
        }
      });
    }, { threshold: 0.4 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [value, duration]);

  return <span ref={ref} data-testid="animated-number">{display}</span>;
};

const TresImmoPage = () => {
  const { t, lang, setLang } = useLanguage();
  const [lightbox, setLightbox] = useState({ open: false, src: '', idx: 0 });
  const [unitModal, setUnitModal] = useState({ open: false, unit: null });
  const [showCookie, setShowCookie] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '', newsletter: false });
  const [formStatus, setFormStatus] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [inquiryForm, setInquiryForm] = useState({ show: false, data: { name: '', email: '', phone: '', message: '' }, status: null, loading: false });
  const [navHidden, setNavHidden] = useState(false);
  const [glowActive, setGlowActive] = useState(false);
  const [ctaBarDismissed, setCtaBarDismissed] = useState(
    () => typeof localStorage !== 'undefined' && localStorage.getItem('tres_cta_dismissed') === '1'
  );
  const [ctaInContactSection, setCtaInContactSection] = useState(false);
  const [ctaShouldShow, setCtaShouldShow] = useState(false);
  const [waTooltipShown, setWaTooltipShown] = useState(false);
  const [waTooltipDismissed, setWaTooltipDismissed] = useState(false);
  const [exitIntent, setExitIntent] = useState({ open: false });
  const [units, setUnits] = useState(mockUnits);
  const [unitsLoading, setUnitsLoading] = useState(true);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [filterRooms, setFilterRooms] = useState('all');
  const [filterFloor, setFilterFloor] = useState('all');
  const [sortBy, setSortBy] = useState('id');
  const particlesRef = useRef(null);
  const scrollFrameRef = useRef(null);

  // Gallery captions from translations
  const vizCaptions = [t('viz_caption1'), t('viz_caption2'), t('viz_caption3'), t('viz_caption4'), t('viz_caption5'), t('viz_caption6')];

  // Lage items from translations
  const lageItems = [
    { icon: 'arrow', title: t('lage_item1_title'), text: t('lage_item1_text') },
    { icon: 'train', title: t('lage_item2_title'), text: t('lage_item2_text') },
    { icon: 'nature', title: t('lage_item3_title'), text: t('lage_item3_text') },
  ];

  // Ausstattung items from translations
  const ausstattungItems = [
    { icon: 'layers', title: t('ausstattung_item1_title'), text: t('ausstattung_item1_text') },
    { icon: 'terrace', title: t('ausstattung_item2_title'), text: t('ausstattung_item2_text') },
    { icon: 'shield', title: t('ausstattung_item3_title'), text: t('ausstattung_item3_text') },
  ];

  // Fetch real units from API
  useEffect(() => {
    const fetchUnits = async () => {
      try {
        const resp = await axios.get(`${API}/units`);
        const data = resp.data;
        if (data && data.records && data.records.length > 0) {
          const formatted = data.records.map(r => {
            const floorNum = parseInt(r.floor, 10);
            const isNaN_ = isNaN(floorNum);
            const floorLabel = isNaN_ ? String(r.floor) : floorNum < 0 ? 'UG' : floorNum === 0 ? 'EG' : `${floorNum}. OG`;
            const roomsLabel = r.rooms ? `${r.rooms} Zi.` : '–';
            const isSold = r.status_slug === 'sold';
            const areaDisplay = r.area_total != null ? r.area_total : r.area;
            const areaLabel = isSold ? '' : areaDisplay ? `${String(areaDisplay).replace('.', ',')} m²` : '–';
            let priceLabel = isSold ? '' : 'Auf Anfrage';
            if (!isSold && r.price && r.status_slug !== 'reserved' && r.price <= 1000000) {
              priceLabel = `€ ${Number(r.price).toLocaleString('de-DE', { maximumFractionDigits: 0 })}`;
            }
            return {
              id: r.unit_id,
              propstackId: r.propstack_id,
              floor: floorLabel,
              rooms: roomsLabel,
              area: areaLabel,
              price: priceLabel,
              status: r.status_slug || 'available',
              statusName: r.status_slug === 'reserved' ? 'Reserviert' : r.status_slug === 'sold' ? 'Verkauft' : '',
              floorplanUrl: r.floorplan_url,
              exposeUrl: r.expose_url,
              publicUrl: r.public_url,
              thumbnail: r.thumbnail,
            };
          });
          setUnits(formatted);
        }
      } catch (err) {
        console.log('Using mock data for units', err);
      } finally {
        setUnitsLoading(false);
      }
    };
    fetchUnits();
  }, []);

  // Generate particles
  useEffect(() => {
    const pc = particlesRef.current;
    if (!pc) return;
    for (let i = 0; i < 30; i++) {
      const p = document.createElement('div');
      p.className = 'particle';
      const size = 1.2 + Math.random() * 2.8;
      p.style.cssText = `left:${Math.random()*100}%;bottom:-8px;--dx:${(Math.random()-.5)*100}px;animation-duration:${14+Math.random()*18}s;animation-delay:${Math.random()*24}s;width:${size}px;height:${size}px;`;
      pc.appendChild(p);
    }
  }, []);

  // Scroll reveal
  useEffect(() => {
    const els = document.querySelectorAll('.section-label,.projekt-headline,.projekt-text,.card,.rimmo-note,.viz-item,.viz-note,.info-item,.cs-label,.cs-headline,.cs-text,.contact-form,.cs-divider,.wa-btn,.wa-note');
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); });
    }, { threshold: 0.12 });
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  // Nav visibility + scroll progress on scroll
  useEffect(() => {
    const updateOnScroll = () => {
      setNavHidden(window.scrollY > window.innerHeight * 0.6);
      const total = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(total > 0 ? (window.scrollY / total) * 100 : 0);
      // Sticky CTA: show only after user has scrolled significantly (> 1.5x viewport)
      setCtaShouldShow(window.scrollY > window.innerHeight * 1.5);
      // Hide sticky CTA when user reached the contact form (coming-soon) or footer
      const cs = document.getElementById('coming-soon');
      if (cs) {
        const rect = cs.getBoundingClientRect();
        setCtaInContactSection(rect.top < window.innerHeight * 0.7);
      }
      scrollFrameRef.current = null;
    };
    const onScroll = () => {
      if (scrollFrameRef.current === null) scrollFrameRef.current = requestAnimationFrame(updateOnScroll);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (scrollFrameRef.current !== null) cancelAnimationFrame(scrollFrameRef.current);
    };
  }, []);

  // Letter glow after intro
  useEffect(() => {
    const timer = setTimeout(() => setGlowActive(true), 4200);
    return () => clearTimeout(timer);
  }, []);

  // Show WhatsApp-FAB tooltip once after 5s (if not dismissed)
  useEffect(() => {
    const shown = sessionStorage.getItem('tres_wa_tooltip_shown') === '1';
    if (shown) return;
    const t1 = setTimeout(() => {
      setWaTooltipShown(true);
      sessionStorage.setItem('tres_wa_tooltip_shown', '1');
    }, 5000);
    return () => clearTimeout(t1);
  }, []);

  // Auto-hide WA tooltip after another 7s
  useEffect(() => {
    if (!waTooltipShown || waTooltipDismissed) return;
    const t = setTimeout(() => setWaTooltipDismissed(true), 7000);
    return () => clearTimeout(t);
  }, [waTooltipShown, waTooltipDismissed]);

  // Exit-intent detection (desktop only, once per session)
  useEffect(() => {
    if (window.innerWidth < 900) return;
    if (sessionStorage.getItem('tres_exit_intent_shown') === '1') return;
    const onLeave = (e) => {
      if (e.clientY <= 0 && e.relatedTarget === null) {
        setExitIntent({ open: true });
        sessionStorage.setItem('tres_exit_intent_shown', '1');
        document.removeEventListener('mouseout', onLeave);
      }
    };
    // Small delay so it doesn't fire during page load
    const armed = setTimeout(() => document.addEventListener('mouseout', onLeave), 4000);
    return () => { clearTimeout(armed); document.removeEventListener('mouseout', onLeave); };
  }, []);

  const dismissCtaBar = useCallback(() => {
    setCtaBarDismissed(true);
    try { localStorage.setItem('tres_cta_dismissed', '1'); } catch (e) {}
  }, []);

  // reCAPTCHA v3 Token holen
  const getRecaptchaToken = useCallback(async (action) => {
    try {
      const recaptcha = await loadRecaptcha(RECAPTCHA_SITE_KEY);
      if (recaptcha) return await recaptcha.execute(RECAPTCHA_SITE_KEY, { action });
    } catch (e) { console.warn('reCAPTCHA not available:', e); }
    return '';
  }, []);

  // Turnstile – Kontaktformular (immer sichtbar) + GA4 Consent wiederherstellen
  useEffect(() => {
    const saved = localStorage.getItem('tres_cookie_consent');
    if (!saved) {
      const timer = setTimeout(() => setShowCookie(true), 1500);
      return () => clearTimeout(timer);
    }
    // "all" → dauerhaft gespeichert
    if (saved === 'all') {
      loadAnalytics().then(() => {
        if (typeof window.gtag !== 'function') return;
        window.gtag('consent', 'update', {
          analytics_storage: 'granted', ad_storage: 'granted',
          ad_user_data: 'granted', ad_personalization: 'granted'
        });
      });
      return;
    }
    // "essential" mit Ablaufdatum → nach 24h Banner erneut zeigen
    try {
      const parsed = JSON.parse(saved);
      if (parsed.type === 'essential' && Date.now() > parsed.expires) {
        localStorage.removeItem('tres_cookie_consent');
        const timer = setTimeout(() => setShowCookie(true), 1500);
        return () => clearTimeout(timer);
      }
    } catch (_) {
      // altes Format – Banner erneut zeigen
      localStorage.removeItem('tres_cookie_consent');
      const timer = setTimeout(() => setShowCookie(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Body overflow for modals
  useEffect(() => {
    document.body.style.overflow = (lightbox.open || unitModal.open || privacyOpen) ? 'hidden' : '';
  }, [lightbox.open, unitModal.open, privacyOpen]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightbox.open) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') setLightbox({ open: false, src: '', idx: 0 });
      if (e.key === 'ArrowRight') {
        const newIdx = (lightbox.idx + 1) % galleryImages.length;
        setLightbox({ open: true, src: galleryImages[newIdx].full, idx: newIdx });
      }
      if (e.key === 'ArrowLeft') {
        const newIdx = (lightbox.idx - 1 + galleryImages.length) % galleryImages.length;
        setLightbox({ open: true, src: galleryImages[newIdx].full, idx: newIdx });
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightbox]);

  const handleCookie = useCallback((type) => {
    if (type === 'all') {
      localStorage.setItem('tres_cookie_consent', 'all');
    } else {
      // Nur Notwendige: läuft nach 24h ab → Banner erscheint morgen wieder
      const expires = Date.now() + 24 * 60 * 60 * 1000;
      localStorage.setItem('tres_cookie_consent', JSON.stringify({ type: 'essential', expires }));
    }
    setShowCookie(false);
    if (type === 'all') {
      loadAnalytics().then(() => {
        if (typeof window.gtag !== 'function') return;
        window.gtag('consent', 'update', {
          analytics_storage: 'granted',
          ad_storage: 'granted',
          ad_user_data: 'granted',
          ad_personalization: 'granted'
        });
        window.gtag('event', 'page_view', {
          page_location: window.location.href,
          page_title: document.title,
        });
      });
    } else if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', {
          analytics_storage: 'denied',
          ad_storage: 'denied',
          ad_user_data: 'denied',
          ad_personalization: 'denied'
        });
    }
  }, []);

  const handleFormSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) return;
    setFormLoading(true);
    try {
      const recaptchaToken = await getRecaptchaToken('contact_form');
      const finalMessage = formData.message + (formData.newsletter ? "\n\n[Interesse an Baufortschritt-Updates: Ja]" : "");
      const resp = await axios.post(`${API}/lead`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: finalMessage,
        recaptcha_token: recaptchaToken,
      });
      setFormStatus(resp.data.success ? 'success' : 'error');
      if (resp.data.success) trackEvent('generate_lead', { lead_type: 'general_contact', language: lang });
    } catch (err) {
      console.error('Form submit error:', err);
      setFormStatus('error');
    }
    setFormLoading(false);
    setFormData({ name: '', email: '', phone: '', message: '', newsletter: false });
  }, [formData, getRecaptchaToken, lang]);

  const scrollToSection = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  // Handle unit inquiry form submission
  const handleInquirySubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!inquiryForm.data.name || !inquiryForm.data.email) return;
    setInquiryForm(prev => ({ ...prev, loading: true }));
    try {
      const unit = unitModal.unit;
      const recaptchaToken = await getRecaptchaToken('unit_inquiry');
      const resp = await axios.post(`${API}/lead`, {
        name: inquiryForm.data.name,
        email: inquiryForm.data.email,
        phone: inquiryForm.data.phone,
        message: inquiryForm.data.message,
        unit: unit?.id || '',
        propstack_property_id: unit?.propstackId || null,
        recaptcha_token: recaptchaToken,
      });
      setInquiryForm(prev => ({
        ...prev,
        loading: false,
        status: resp.data.success ? 'success' : 'error',
      }));
      if (resp.data.success) trackEvent('generate_lead', { lead_type: 'unit_inquiry', unit_id: unit?.id, language: lang });
    } catch (err) {
      console.error('Inquiry submit error:', err);
      setInquiryForm(prev => ({ ...prev, loading: false, status: 'error' }));
    }
  }, [inquiryForm.data, unitModal.unit, getRecaptchaToken, lang]);

  // Open the inquiry form inside unit modal
  const openInquiryForm = useCallback(() => {
    setInquiryForm({ show: true, data: { name: '', email: '', phone: '', message: '' }, status: null, loading: false });
  }, []);

  // Filtered and sorted units
  const filteredUnits = units.filter(u => {
    if (filterRooms !== 'all' && !u.rooms.startsWith(filterRooms)) return false;
    if (filterFloor !== 'all' && u.floor !== filterFloor) return false;
    return true;
  }).sort((a, b) => {
    if (sortBy === 'price') {
      const pA = parseFloat((a.price || '').replace(/[^\d,]/g,'').replace(',','.')) || 99999999;
      const pB = parseFloat((b.price || '').replace(/[^\d,]/g,'').replace(',','.')) || 99999999;
      return pA - pB;
    }
    if (sortBy === 'area') {
      const aA = parseFloat((a.area || '').replace(/[^\d,]/g,'').replace(',','.')) || 0;
      const aB = parseFloat((b.area || '').replace(/[^\d,]/g,'').replace(',','.')) || 0;
      return aB - aA;
    }
    return a.id.localeCompare(b.id);
  });

  return (
    <div className="tres-page">
      <SeoHead page="home" />
      {/* Scroll progress bar */}
      <div className="scroll-progress" style={{ width: `${scrollProgress}%` }} />

      {/* Background effects */}
      <div className="bg-fixed" />
      <div className="ring" />
      <div className="ring" />
      <div className="ring" />
      <div className="particles" ref={particlesRef} />

      {/* TopNav - Language Switcher + Section Nav */}
      <nav className={`topnav${navHidden ? '' : ' hero-mode'}`}>
        <div className="topnav-logo" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          TRES IMMO
        </div>
        <div className="topnav-links">
          <button onClick={() => scrollToSection('projekt')}>{t('nav_projekt')}</button>
          <button onClick={() => scrollToSection('visualisierungen')}>{t('nav_impressionen')}</button>
          <button onClick={() => scrollToSection('lage')}>{t('nav_lage')}</button>
          <button onClick={() => scrollToSection('ausstattung')}>{t('nav_qualitaet')}</button>
          <button onClick={() => scrollToSection('einheiten')}>{t('nav_einheiten')}</button>
          <button onClick={() => scrollToSection('coming-soon')}>{t('nav_kontakt')}</button>
        </div>
        <div className="topnav-right">
          <div className="lang-switcher">
            {['de','it','en'].map((l, i) => (
              <React.Fragment key={l}>
                {i > 0 && <span>·</span>}
                <button className={lang === l ? 'active' : ''} onClick={() => setLang(l)}>{l.toUpperCase()}</button>
              </React.Fragment>
            ))}
          </div>
          <a className="topnav-wa" onClick={() => trackEvent('whatsapp_click', { placement: 'topnav', language: lang })} href={`https://wa.me/393935652804?text=${encodeURIComponent(t('js_wa_general'))}`} target="_blank" rel="noopener noreferrer">
            <WhatsAppIcon size={12} />
            <span>WhatsApp</span>
          </a>
        </div>
      </nav>

      {/* ═══ HERO ═══ */}
      <section id="hero">
        <p className="location-tag">{t('hero_location')}</p>
        <div className="logo-wrap">
          <div className="orbit-dot" />
          <TresLogo />
        </div>
        <p className="tagline">{t('hero_tagline')}</p>
        <div className="divider" />
        <p className="hero-sub" dangerouslySetInnerHTML={{ __html: t('hero_sub') }} />

        <div className="hero-ctas">
          <button className="hero-cta-btn primary" onClick={() => scrollToSection('einheiten')}>
            <span>{t('hero_cta_units')}</span>
          </button>
          <button className="hero-cta-btn secondary" onClick={() => { trackEvent('expose_request', { placement: 'hero', language: lang }); scrollToSection('coming-soon'); }}>
            <span>{t('hero_cta_expose')}</span>
          </button>
        </div>

        <div className="scroll-hint" onClick={() => scrollToSection('projekt')}>
          <span>{t('hero_scroll')}</span>
          <svg className="chevron" viewBox="0 0 20 12" fill="none"><path d="M1 1L10 10L19 1" stroke="rgba(184,154,94,0.5)" strokeWidth="1.5" strokeLinecap="round"/></svg>
        </div>
      </section>

      {/* ═══ BAUFORTSCHRITT BANNER ═══ */}
      <Link className="bf-banner" to={localizedPath(lang, '/baufortschritt')}>
        <div className="bf-banner-pulse" />
        <span>{t('bf_banner_text')}</span>
      </Link>

      {/* ═══ PROJEKT ═══ */}
      <section id="projekt">
        <p className="section-label">{t('projekt_label')}</p>
        <h2 className="projekt-headline" dangerouslySetInnerHTML={{ __html: t('projekt_headline') }} />
        <p className="projekt-text">{t('projekt_text')}</p>
        <div className="cards">
          <div className="card"><div className="card-number"><AnimatedNumber value={t('card_1_number')} /></div><div className="card-label">{t('card_1_label')}</div><div className="card-desc">{t('card_1_desc')}</div></div>
          <div className="card"><div className="card-number"><AnimatedNumber value={t('card_2_number')} /></div><div className="card-label">{t('card_2_label')}</div><div className="card-desc">{t('card_2_desc')}</div></div>
          <div className="card"><div className="card-number"><AnimatedNumber value={t('card_3_number')} /></div><div className="card-label">{t('card_3_label')}</div><div className="card-desc">{t('card_3_desc')}</div></div>
          <div className="card card-full"><div className="card-desc">{t('card_full')}</div></div>
        </div>
        <div className="rimmo-note">
          <span dangerouslySetInnerHTML={{ __html: t('rimmo_note_pre') }} />
          <a className="rimmo-wa" href={`https://wa.me/393286770955?text=${encodeURIComponent(t('js_wa_rimmo'))}`} target="_blank" rel="noopener noreferrer">
            <WhatsAppIcon /><span>{t('rimmo_wa_label')}</span>
          </a>
        </div>
      </section>

      {/* ═══ VISUALISIERUNGEN ═══ */}
      <section id="visualisierungen">
        <p className="section-label">{t('viz_label')}</p>
        <h2 className="projekt-headline" dangerouslySetInnerHTML={{ __html: t('viz_headline') }} />
        <div className="viz-gallery">
          {galleryImages.map((img, i) => (
            <div key={i} className="viz-item" onClick={() => setLightbox({ open: true, src: img.full, idx: i })}>
              <img src={img.thumb} loading="lazy" width="800" height="600" alt={vizCaptions[i] || img.caption} />
              <div className="viz-caption">{vizCaptions[i] || img.caption}</div>
            </div>
          ))}
        </div>
        <p className="viz-note">{t('viz_note')}</p>
      </section>

      {/* ═══ LAGE ═══ */}
      <section id="lage" className="info-section">
        <div className="info-left">
          <p className="section-label">{t('lage_label')}</p>
          <h2 className="projekt-headline" dangerouslySetInnerHTML={{ __html: t('lage_headline') }} />
        </div>
        <div className="info-list">
          {lageItems.map((item, i) => (
            <div key={i} className="info-item" style={{ transitionDelay: `${i * 0.15}s` }}>
              <h3><InfoIcon type={item.icon} /><span>{item.title}</span></h3>
              <p>{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Aktuelles Baustellenfoto */}
      <div style={{ position: 'relative', height: 'clamp(280px, 40vw, 500px)', overflow: 'hidden' }}>
        <img
          src="/img/baufortschritt-aktuell-august-2026.png"
          alt="Baufortschritt Am Waldrand Oberbozen – Decke über dem ersten Obergeschoss am 8. August 2026"
          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 60%' }}
        />
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to bottom, rgba(13,31,23,0.35) 0%, transparent 30%, transparent 60%, rgba(13,31,23,0.7) 100%)'
        }} />
        <div style={{
          position: 'absolute', bottom: '2rem', left: 'clamp(1.5rem, 6vw, 5rem)',
          display: 'flex', flexDirection: 'column', gap: '0.3rem'
        }}>
          <span style={{ fontSize: '0.6rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: 'var(--gold)' }}>
          08.08.2026
          </span>
          <span style={{ fontSize: 'clamp(1rem, 2.5vw, 1.4rem)', fontFamily: "'Cormorant Garamond', serif", color: 'var(--cream)', fontStyle: 'italic' }}>
            Decke über dem 1. OG · Oberbozen am Ritten
          </span>
        </div>
        <a href={localizedPath(lang, '/baufortschritt')} style={{
          position: 'absolute', bottom: '2rem', right: 'clamp(1.5rem, 6vw, 5rem)',
          fontSize: '0.62rem', letterSpacing: '0.25em', textTransform: 'uppercase',
          color: 'var(--cream)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.4rem',
          opacity: 0.8
        }}>
          Alle Updates
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
        </a>
      </div>

      {/* ═══ AUSSTATTUNG ═══ */}
      <section id="ausstattung" className="info-section">
        <div className="info-left">
          <p className="section-label">{t('ausstattung_label')}</p>
          <h2 className="projekt-headline" dangerouslySetInnerHTML={{ __html: t('ausstattung_headline') }} />
        </div>
        <div className="info-list">
          {ausstattungItems.map((item, i) => (
            <div key={i} className="info-item" style={{ transitionDelay: `${i * 0.15}s` }}>
              <h3><InfoIcon type={item.icon} /><span>{item.title}</span></h3>
              <p>{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ EINHEITEN ═══ */}
      <section id="einheiten">
        <p className="section-label" style={{ textAlign: 'center', marginBottom: '0.5rem' }}>{t('units_project_label')}</p>
        <h2 className="projekt-headline" style={{ textAlign: 'center', fontSize: 'clamp(2rem, 6vw, 4rem)', marginBottom: '1.5rem' }} dangerouslySetInnerHTML={{ __html: t('units_headline') }} />

        {(() => {
          const availableUnitsCount = units.filter(u => u.status === 'available').length;
          return availableUnitsCount > 0 && availableUnitsCount <= 10 && (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div className="urgency-badge">
                <div className="urgency-badge-pulse" />
                <span>{t('urgency_only_few_left').replace('{count}', String(availableUnitsCount))}</span>
              </div>
            </div>
          );
        })()}

        {/* Filter controls */}
        <div className="units-filters">
          <div className="filter-group">
            <label className="filter-label">{t('filter_rooms') || 'Zimmer'}</label>
            <select className="filter-select" value={filterRooms} onChange={e => setFilterRooms(e.target.value)}>
              <option value="all">{t('filter_all') || 'Alle'}</option>
              <option value="2">2 {t('filter_rooms') || 'Zimmer'}</option>
              <option value="3">3 {t('filter_rooms') || 'Zimmer'}</option>
              <option value="4">4 {t('filter_rooms') || 'Zimmer'}</option>
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">{t('filter_floor') || 'Etage'}</label>
            <select className="filter-select" value={filterFloor} onChange={e => setFilterFloor(e.target.value)}>
              <option value="all">{t('filter_all') || 'Alle'}</option>
              <option value="UG">UG</option>
              <option value="EG">EG</option>
              <option value="1. OG">1. OG</option>
              <option value="2. OG">2. OG</option>
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">{t('filter_sort') || 'Sortierung'}</label>
            <select className="filter-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
              <option value="id">{t('filter_sort_unit') || 'Einheit'}</option>
              <option value="price">{t('filter_sort_price') || 'Preis'}</option>
              <option value="area">{t('filter_sort_area') || 'Fläche'}</option>
            </select>
          </div>
        </div>

        <div className="units-table-wrap">
          {unitsLoading ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--muted)', fontSize: '0.85rem', letterSpacing: '0.1em' }}>
              {t('units_loading') || 'Lade Einheiten...'}
            </div>
          ) : (
          <table className="units-table">
            <thead>
              <tr>
                <th><span className="th-full">{t('th_unit')}</span><span className="th-short">#</span></th>
                <th>{t('th_floor')}</th>
                <th><span className="th-full">{t('th_rooms')}</span><span className="th-short">Z</span></th>
                <th><span className="th-full">{t('th_area')}</span><span className="th-short">m²</span></th>
                <th>{t('th_price')}</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredUnits.map(u => (
                <tr key={u.id} className={u.status === 'sold' ? 'sold' : ''} onClick={() => u.status !== 'sold' && setUnitModal({ open: true, unit: u })}>
                  <td><strong>{u.id}</strong></td>
                  <td>{u.floor}</td>
                  <td>{u.rooms}</td>
                  <td>{u.area}</td>
                  <td>{u.price}</td>
                  <td>
                    {u.status === 'available' ? (
                      <span className="status-badge available status-dot-only" title="In Vermarktung"><span className="status-dot" /></span>
                    ) : (
                      <span className={`status-badge ${u.status}`}><span className="status-dot" /> {u.statusName}</span>
                    )}
                  </td>
                  <td>
                    {u.status !== 'sold' && (
                      <div className="unit-actions-cell">
                        <Link className="unit-details-link" to={localizedPath(lang, `/einheiten/${u.id.toLowerCase()}`)} onClick={() => trackEvent('view_unit', { unit_id: u.id, language: lang })}>Details</Link>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          )}
        </div>

        {/* Mobile Card-Layout (shown only on small screens via CSS) */}
        {!unitsLoading && (
          <div className="units-cards" data-testid="units-cards-mobile">
            {filteredUnits.map(u => (
              <article
                key={u.id}
                className={`unit-card${u.status === 'sold' ? ' sold' : ''}${u.status === 'reserved' ? ' reserved' : ''}`}
                onClick={() => u.status !== 'sold' && setUnitModal({ open: true, unit: u })}
                data-testid={`unit-card-${u.id}`}
              >
                <div className="unit-card-top" style={(u.status === 'sold' || u.status === 'reserved') ? { borderBottom: 'none', paddingBottom: 0, marginBottom: 0 } : {}}>
                  <div className="unit-card-id">
                    <span className="unit-card-id-label">Einheit</span>
                    <strong>{u.id}</strong>
                  </div>
                  {u.status === 'available' ? (
                    <span className="status-badge available"><span className="status-dot" /> {u.statusName || 'Verfügbar'}</span>
                  ) : (
                    <span className={`status-badge ${u.status}`}><span className="status-dot" /> {u.statusName || (u.status === 'sold' ? 'Verkauft' : 'Reserviert')}</span>
                  )}
                </div>
                {u.status !== 'sold' && u.status !== 'reserved' && (
                  <div className="unit-card-grid">
                    <div><span className="unit-card-k">{t('unit_card_floor')}</span><span className="unit-card-v">{u.floor}</span></div>
                    <div><span className="unit-card-k">{t('unit_card_rooms')}</span><span className="unit-card-v">{u.rooms}</span></div>
                    <div><span className="unit-card-k">{t('unit_card_area')}</span><span className="unit-card-v">{u.area}</span></div>
                    <div><span className="unit-card-k">{t('unit_card_price')}</span><span className="unit-card-v unit-card-price">{u.price}</span></div>
                  </div>
                )}
                {u.status !== 'sold' && (
                  <Link className="unit-card-cta" to={localizedPath(lang, `/einheiten/${u.id.toLowerCase()}`)} onClick={() => trackEvent('view_unit', { unit_id: u.id, language: lang })}>
                    <span>{t('unit_card_details')}</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                  </Link>
                )}
              </article>
            ))}
          </div>
        )}
        <p className="units-note">{t('units_note')}</p>
      </section>

      <p className="seo-text">{t('seo_text')}</p>

      {/* ═══ KONTAKT ═══ */}
      <section id="coming-soon">
        <p className="cs-label">{t('cs_label')}</p>
        <h2 className="cs-headline" dangerouslySetInnerHTML={{ __html: t('cs_headline') }} />
        <p className="cs-text">{t('cs_text')}</p>

        <form className="contact-form" onSubmit={handleFormSubmit}>
          <div className="form-group">
            <label className="form-label" htmlFor="contact-name">{t('form_name')}</label>
            <input id="contact-name" name="name" type="text" className="form-input" placeholder={t('form_name')} autoComplete="name" required value={formData.name} onChange={e => setFormData(p => ({...p, name: e.target.value}))} />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="contact-email">{t('form_email')}</label>
            <input id="contact-email" name="email" type="email" className="form-input" placeholder={t('form_email')} autoComplete="email" required value={formData.email} onChange={e => setFormData(p => ({...p, email: e.target.value}))} />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="contact-phone">{t('form_phone')}</label>
            <input id="contact-phone" name="phone" type="tel" className="form-input" placeholder={t('form_phone')} autoComplete="tel" value={formData.phone} onChange={e => setFormData(p => ({...p, phone: e.target.value}))} />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="contact-message">{t('form_message')}</label>
            <textarea id="contact-message" name="message" className="form-input" placeholder={t('form_message')} value={formData.message} onChange={e => setFormData(p => ({...p, message: e.target.value}))} />
          </div>
          <div className="form-group" style={{ display: 'flex', alignItems: 'flex-start', gap: '0.8rem', marginTop: '1.2rem', marginBottom: '1.8rem', cursor: 'pointer', userSelect: 'none' }} onClick={() => setFormData(p => ({...p, newsletter: !p.newsletter}))}>
            <input type="checkbox" id="newsletter" style={{ accentColor: 'var(--gold)', cursor: 'pointer', marginTop: '0.2rem' }} checked={formData.newsletter || false} readOnly />
            <label htmlFor="newsletter" style={{ fontSize: '0.75rem', color: 'var(--muted)', cursor: 'pointer', fontWeight: 300, lineHeight: 1.4 }}>
              {t('form_newsletter_label')}
            </label>
          </div>
          {!formStatus && (
            <button type="submit" className="form-submit" disabled={formLoading}>
              <span>{formLoading ? t('form_sending') : t('form_submit')}</span>
              {!formLoading && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>}
            </button>
          )}
        </form>
        {formStatus === 'success' && <div className="form-response success">{t('form_success')}</div>}
        {formStatus === 'error' && <div className="form-response error">{t('form_error')}</div>}

        <div className="cs-divider" />
        <p className="wa-or-via">{t('js_wa_or_via')}</p>
        <a className="wa-btn" onClick={() => trackEvent('whatsapp_click', { placement: 'contact', language: lang })} href={`https://wa.me/393935652804?text=${encodeURIComponent(t('js_wa_general'))}`} target="_blank" rel="noopener noreferrer">
          <svg className="wa-icon" viewBox="0 0 24 24" fill="none">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" fill="currentColor"/>
            <path d="M12 0C5.373 0 0 5.373 0 12c0 2.124.554 4.118 1.528 5.845L.057 23.776a.5.5 0 00.636.599l6.097-1.995A11.956 11.956 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.9 0-3.683-.516-5.21-1.415l-.374-.22-3.876 1.269 1.22-3.77-.238-.38A10 10 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" fill="currentColor" fillOpacity=".8"/>
          </svg>
          <span>WhatsApp Chat</span>
        </a>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer className="tres-footer">
        <div className="footer-inner">
          <div className="footer-col">
            <div>
              <div className="footer-logo-text">TRES IMMO</div>
              <div className="footer-logo-sub">Wohnen am Waldrand</div>
            </div>
            <p className="footer-desc">{t('footer_desc')}</p>
          </div>
          <div className="footer-divider-v" />
          <div className="footer-col footer-col-right">
            <div className="footer-contact-label">{t('footer_contact_label')}</div>
            <a className="footer-link" href="tel:+393935652804">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 10.8 19.79 19.79 0 01.22 2.18 2 2 0 012.2 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92z"/></svg>
              +39 393 565 2804
            </a>
            <a className="footer-link" href="https://wa.me/393935652804" target="_blank" rel="noopener noreferrer">
              <WhatsAppIcon />
              <span>WhatsApp</span>
            </a>
            <a className="footer-link" href="mailto:info@tres.immo">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
              info@tres.immo
            </a>
            <a className="footer-wa-cta" onClick={() => trackEvent('whatsapp_click', { placement: 'footer', language: lang })} href={`https://wa.me/393935652804?text=${encodeURIComponent(t('js_wa_footer_cta'))}`} target="_blank" rel="noopener noreferrer">
              <WhatsAppIcon />
              <span>{t('footer_cta')}</span>
            </a>
            <a href={localizedPath(lang, '/baufortschritt')} className="footer-link" style={{ marginTop: '0.3rem' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/></svg>
              <span>{lang === 'de' ? 'Baufortschritt' : lang === 'it' ? 'Avanzamento Lavori' : 'Construction Progress'}</span>
            </a>
          </div>
        </div>
        <div className="footer-bottom">
          {'\u00A9'} {new Date().getFullYear()} TRES IMMO {'\u00A0\u00B7\u00A0'} Oberbozen, Ritten, S{'\u00FC'}dtirol {'\u00A0\u00B7\u00A0'} {lang === 'it' ? 'Tutti i diritti riservati' : lang === 'en' ? 'All rights reserved' : 'Alle Rechte vorbehalten'} {'\u00A0|\u00A0'}
          <button onClick={() => setPrivacyOpen(true)} style={{ background: 'none', border: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit', letterSpacing: 'inherit' }}>{lang === 'it' ? 'Privacy & Impressum' : lang === 'en' ? 'Privacy & Imprint' : 'Datenschutz & Impressum'}</button>
        </div>
        <div className="footer-partners">
          <a href="https://www.hafner-ec.com" target="_blank" rel="noopener" className="footer-partner-link">Hafner EC</a>
          <span className="footer-partner-sep">·</span>
          <a href="https://www.amadeus.immo" target="_blank" rel="noopener" className="footer-partner-link">Amadeus Immobilien</a>
        </div>
      </footer>

      {/* ═══ WHATSAPP FAB (Floating Action Button) ═══ */}
      <a
        className={`wa-fab${waTooltipShown && !waTooltipDismissed ? ' tooltip-visible' : ''}`}
        onClick={() => trackEvent('whatsapp_click', { placement: 'floating_button', language: lang })}
        href={`https://wa.me/393935652804?text=${encodeURIComponent(t('js_wa_general'))}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t('wa_fab_label')}
        data-testid="whatsapp-fab"
      >
        <span className="wa-fab-pulse" aria-hidden="true" />
        <span className="wa-fab-icon" aria-hidden="true">
          <WhatsAppIcon size={26} />
        </span>
        <span className="wa-fab-tooltip">
          {t('wa_fab_tooltip')}
          <button
            className="wa-fab-tooltip-close"
            type="button"
            aria-label="Close"
            data-testid="wa-fab-tooltip-close"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setWaTooltipDismissed(true); }}
          >×</button>
        </span>
      </a>

      {/* ═══ EXIT-INTENT MODAL (Desktop only, once per session) ═══ */}
      {exitIntent.open && (
        <div className="exit-modal" data-testid="exit-intent-modal" onClick={(e) => { if (e.target === e.currentTarget) setExitIntent({ open: false }); }}>
          <div className="exit-modal-content">
            <button
              className="exit-modal-close"
              data-testid="exit-modal-close-btn"
              aria-label="Close"
              onClick={() => setExitIntent({ open: false })}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
            <p className="exit-modal-eyebrow">{t('exit_modal_eyebrow')}</p>
            <h3 className="exit-modal-title">{t('exit_modal_title')}</h3>
            <p className="exit-modal-text">{t('exit_modal_text')}</p>
            <div className="exit-modal-actions">
              <button
                className="exit-modal-cta"
                data-testid="exit-modal-cta-btn"
                onClick={() => { setExitIntent({ open: false }); scrollToSection('coming-soon'); }}
              >
                {t('exit_modal_cta')}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
              </button>
              <button
                className="exit-modal-skip"
                data-testid="exit-modal-skip-btn"
                onClick={() => setExitIntent({ open: false })}
              >
                {t('exit_modal_close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ STICKY CTA BAR ═══ */}
      <div
        className={`sticky-cta${ctaShouldShow && !ctaInContactSection && !ctaBarDismissed ? ' show' : ''}`}
        data-testid="sticky-cta-bar"
      >
        <div className="sticky-cta-inner">
          <span className="sticky-cta-text">{t('sticky_cta_text')}</span>
          <button
            className="sticky-cta-btn"
            data-testid="sticky-cta-btn"
            onClick={() => scrollToSection('coming-soon')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            <span className="sticky-cta-btn-long">{t('sticky_cta_btn')}</span>
            <span className="sticky-cta-btn-short">{t('sticky_cta_btn_short')}</span>
          </button>
          <button
            className="sticky-cta-close"
            data-testid="sticky-cta-close-btn"
            aria-label={t('sticky_cta_close')}
            title={t('sticky_cta_close')}
            onClick={dismissCtaBar}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      </div>

      {/* ═══ LIGHTBOX ═══ */}
      <div className={`lightbox${lightbox.open ? ' open' : ''}`} onClick={(e) => { if (e.target === e.currentTarget) setLightbox({ open: false, src: '', idx: 0 }); }}>
        <button className="lightbox-close" onClick={() => setLightbox({ open: false, src: '', idx: 0 })}>✕</button>
        {lightbox.open && (
          <>
            <button className="lightbox-nav lightbox-prev" onClick={(e) => { e.stopPropagation(); const newIdx = (lightbox.idx - 1 + galleryImages.length) % galleryImages.length; setLightbox({ open: true, src: galleryImages[newIdx].full, idx: newIdx }); }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <img src={lightbox.src} alt="Fullscreen" />
            <button className="lightbox-nav lightbox-next" onClick={(e) => { e.stopPropagation(); const newIdx = (lightbox.idx + 1) % galleryImages.length; setLightbox({ open: true, src: galleryImages[newIdx].full, idx: newIdx }); }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
            <div className="lightbox-counter">{lightbox.idx + 1} / {galleryImages.length}</div>
          </>
        )}
      </div>

      {/* ═══ UNIT MODAL ═══ */}
      <div className={`unit-modal${unitModal.open ? ' show' : ''}`} onClick={(e) => { if (e.target === e.currentTarget) { setUnitModal({ open: false, unit: null }); setInquiryForm({ show: false, data: { name: '', email: '', phone: '', message: '' }, status: null, loading: false }); } }}>
        {unitModal.unit && (
          <div className={`um-content${(unitModal.unit?.floorplanUrl || unitModal.unit?.thumbnail) && !inquiryForm.show ? ' has-image' : ''}`}>
            <div className="um-header">
              <div className="um-header-left">
                <span className="um-header-project">Waldrand Oberbozen</span>
                <span className="um-header-unit">{unitModal.unit.id}</span>
              </div>
              <button className="um-close" onClick={() => { setUnitModal({ open: false, unit: null }); setInquiryForm({ show: false, data: { name: '', email: '', phone: '', message: '' }, status: null, loading: false }); }}>&#10005;</button>
            </div>

            {/* ── INQUIRY FORM VIEW ── */}
            {inquiryForm.show ? (
              <div className="um-inquiry-wrap">
                <div className="um-inquiry-header">
                  <h3 className="um-inquiry-title">{t('inquiry_form_title')} {unitModal.unit.id}</h3>
                  <p className="um-inquiry-subtitle">{t('inquiry_form_subtitle')}</p>
                </div>
                {!inquiryForm.status ? (
                  <form className="um-inquiry-form" onSubmit={handleInquirySubmit}>
                    <label className="form-label" htmlFor="inquiry-name">{t('inquiry_form_name')}</label>
                    <input
                      id="inquiry-name"
                      name="name"
                      data-testid="inquiry-name"
                      type="text"
                      autoComplete="name"
                      className="um-inquiry-input"
                      placeholder={t('inquiry_form_name')}
                      required
                      value={inquiryForm.data.name}
                      onChange={e => setInquiryForm(prev => ({ ...prev, data: { ...prev.data, name: e.target.value } }))}
                    />
                    <label className="form-label" htmlFor="inquiry-email">{t('inquiry_form_email')}</label>
                    <input
                      id="inquiry-email"
                      name="email"
                      data-testid="inquiry-email"
                      type="email"
                      autoComplete="email"
                      className="um-inquiry-input"
                      placeholder={t('inquiry_form_email')}
                      required
                      value={inquiryForm.data.email}
                      onChange={e => setInquiryForm(prev => ({ ...prev, data: { ...prev.data, email: e.target.value } }))}
                    />
                    <label className="form-label" htmlFor="inquiry-phone">{t('inquiry_form_phone')}</label>
                    <input
                      id="inquiry-phone"
                      name="phone"
                      data-testid="inquiry-phone"
                      type="tel"
                      autoComplete="tel"
                      className="um-inquiry-input"
                      placeholder={t('inquiry_form_phone')}
                      value={inquiryForm.data.phone}
                      onChange={e => setInquiryForm(prev => ({ ...prev, data: { ...prev.data, phone: e.target.value } }))}
                    />
                    <label className="form-label" htmlFor="inquiry-message">{t('inquiry_form_message')}</label>
                    <textarea
                      id="inquiry-message"
                      name="message"
                      data-testid="inquiry-message"
                      className="um-inquiry-input um-inquiry-textarea"
                      placeholder={t('inquiry_form_message')}
                      value={inquiryForm.data.message}
                      onChange={e => setInquiryForm(prev => ({ ...prev, data: { ...prev.data, message: e.target.value } }))}
                    />
                    <div className="um-inquiry-actions">
                      <button type="button" className="um-inquiry-back" onClick={() => setInquiryForm(prev => ({ ...prev, show: false }))} data-testid="inquiry-back-btn">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                        {t('inquiry_form_back')}
                      </button>
                      <button type="submit" className="um-inquiry-submit" disabled={inquiryForm.loading} data-testid="inquiry-submit-btn">
                        {inquiryForm.loading ? t('inquiry_form_sending') : t('inquiry_form_submit')}
                        {!inquiryForm.loading && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>}
                      </button>
                    </div>
                  </form>
                ) : inquiryForm.status === 'success' ? (
                  <div className="um-inquiry-result success" data-testid="inquiry-success">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                    <p>{t('inquiry_form_success')}</p>
                    <button className="um-inquiry-close-btn" onClick={() => { setUnitModal({ open: false, unit: null }); setInquiryForm({ show: false, data: { name: '', email: '', phone: '', message: '' }, status: null, loading: false }); }}>OK</button>
                  </div>
                ) : (
                  <div className="um-inquiry-result error" data-testid="inquiry-error">
                    <p>{t('inquiry_form_error')}</p>
                    <button className="um-inquiry-back" onClick={() => setInquiryForm(prev => ({ ...prev, status: null }))}>
                      {t('inquiry_form_back')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
            <>
            {(unitModal.unit.floorplanUrl || unitModal.unit.thumbnail) && (
              <div className="um-img-wrap">
                <img className="um-img" src={unitModal.unit.floorplanUrl || unitModal.unit.thumbnail} alt={`Grundriss ${unitModal.unit.id}`} loading="lazy" />
              </div>
            )}
            <div className="um-body" style={{ padding: 0 }}>
              <div className="um-stats">
                <div className="um-stat-item">
                  <svg className="um-stat-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v16"/><path d="M9 21v-6h6v6"/><path d="M3 10h18"/></svg>
                  <span className="um-stat-label">Etage</span>
                  <span className="um-stat-val">{unitModal.unit.floor}</span>
                </div>
                <div className="um-stat-item">
                  <svg className="um-stat-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 9V6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v3"/><path d="M2 11v5a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-5a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1Z"/><path d="M6 11V9"/></svg>
                  <span className="um-stat-label">Zimmer</span>
                  <span className="um-stat-val">{unitModal.unit.rooms}</span>
                </div>
                <div className="um-stat-item">
                  <svg className="um-stat-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
                  <span className="um-stat-label">Fläche</span>
                  <span className="um-stat-val">{unitModal.unit.area}</span>
                </div>
                <div className="um-stat-item">
                  <span className="um-stat-icon" style={{ fontSize: '1.2rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>&euro;</span>
                  <span className="um-stat-label">Preis</span>
                  <span className="um-stat-val">{unitModal.unit.price}</span>
                </div>
              </div>
              <div className="um-actions">
                <button className="um-action-btn" style={{ opacity: unitModal.unit?.floorplanUrl ? 1 : 0.35, pointerEvents: unitModal.unit?.floorplanUrl ? 'auto' : 'none' }} onClick={() => unitModal.unit?.floorplanUrl && window.open(unitModal.unit.floorplanUrl, '_blank')}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18M3 9h6M3 15h6"/></svg>
                  <span>Plan</span>
                </button>
                <button className="um-action-btn" data-testid="unit-inquiry-btn" onClick={() => { trackEvent('select_unit', { unit_id: unitModal.unit.id, language: lang }); openInquiryForm(); }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                  <span>{t('modal_action_inquiry')}</span>
                </button>
                <a className="um-action-btn um-action-wa" onClick={() => trackEvent('whatsapp_click', { placement: 'unit_modal', unit_id: unitModal.unit.id, language: lang })} href={`https://wa.me/393935652804?text=${encodeURIComponent(t('js_wa_unit_inquiry').replace('{unit}', unitModal.unit.id))}`} target="_blank" rel="noopener noreferrer">
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM11.998 2.003C6.477 2.003 2 6.48 2 12a9.998 9.998 0 001.362 5.022L2.003 22l5.14-1.347A9.979 9.979 0 0012 22c5.522 0 10-4.477 10-10S17.52 2.003 12 2.003zm0 18.184a8.168 8.168 0 01-4.168-1.143l-.299-.177-3.052.8.818-2.981-.195-.307A8.185 8.185 0 013.815 12c0-4.511 3.672-8.182 8.183-8.182S20.18 7.489 20.18 12s-3.671 8.187-8.182 8.187z"/></svg>
                  <span>WhatsApp</span>
                </a>
                <button className="um-action-btn" onClick={() => { navigator.clipboard.writeText(window.location.href); }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
                  <span>{t('modal_action_share')}</span>
                </button>
              </div>
              {unitModal.unit.exposeUrl && (
                <a className="um-expose-link" href={unitModal.unit.exposeUrl} target="_blank" rel="noopener noreferrer">
                  Exposé herunterladen
                </a>
              )}
            </div>
            </>
            )}
          </div>
        )}
      </div>

      {/* ═══ COOKIE BANNER ═══ */}
      <div className={`cookie-banner${showCookie ? ' show' : ''}`}>
        <span className="cookie-text" dangerouslySetInnerHTML={{ __html: t('cookie_text_short') }} />
        <div className="cookie-btns">
          <button className="cookie-btn cookie-btn-secondary" onClick={() => handleCookie('essential')}>{t('cookie_btn_essential')}</button>
          <button className="cookie-btn cookie-btn-primary" onClick={() => handleCookie('all')}>{t('cookie_btn_all')}</button>
        </div>
      </div>

      {/* ═══ PRIVACY MODAL ═══ */}
      <div className={`privacy-modal${privacyOpen ? ' show' : ''}`} onClick={(e) => { if (e.target === e.currentTarget) setPrivacyOpen(false); }}>
        <div className="privacy-modal-content">
          <div className="privacy-modal-header">
            <div className="privacy-modal-title">Datenschutz &amp; Impressum</div>
            <button className="privacy-modal-close" onClick={() => setPrivacyOpen(false)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div className="privacy-modal-body">
            <h3 style={{ color: 'var(--gold)', fontFamily: "'Cormorant Garamond', serif", marginBottom: '1rem' }}>Impressum</h3>
            <p style={{ fontSize: '0.7rem', color: 'var(--muted)', marginBottom: '0.5rem' }}>Angaben gem. {'\u00A7'} 5 TMG / Information nach dem italienischen E-Commerce-Gesetz (Gesetzesvertretendes Dekret 70/2003).</p>
            <p><strong>TRES IMMO GMBH</strong><br/>Via Rio Sinigo 60<br/>39012 Meran (BZ)<br/>Italien</p>
            <p style={{ marginTop: '0.8rem' }}><strong>Gesetzlicher Vertreter:</strong> T. Sebastian</p>
            <p style={{ marginTop: '0.8rem' }}><strong>Kontakt</strong><br/>Telefon: <a href="tel:+393935652804" style={{ color: 'var(--gold)' }}>+39 393 565 2804</a><br/>E-Mail: <a href="mailto:info@tres.immo" style={{ color: 'var(--gold)' }}>info@tres.immo</a><br/>PEC: tresimmo@pec.it<br/>Webseite: <a href="https://tres.immo" style={{ color: 'var(--gold)' }}>tres.immo</a></p>
            <p style={{ marginTop: '0.8rem' }}><strong>Rechtliche Informationen</strong><br/>MwSt. / P.IVA: 03339300216</p>

            <h3 style={{ color: 'var(--gold)', fontFamily: "'Cormorant Garamond', serif", marginTop: '2rem', marginBottom: '1rem' }}>Datenschutzerkl{'\u00E4'}rung</h3>

            <h4 style={{ color: 'var(--cream)', marginTop: '1.2rem', marginBottom: '0.5rem', fontSize: '0.85rem' }}>1. Datenschutz auf einen Blick</h4>
            <p><strong>Allgemeine Hinweise</strong><br/>Die folgenden Hinweise geben einen einfachen {'\u00DC'}berblick dar{'\u00FC'}ber, was mit Ihren personenbezogenen Daten passiert, wenn Sie unsere Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie pers{'\u00F6'}nlich identifiziert werden k{'\u00F6'}nnen.</p>
            <p style={{ marginTop: '0.8rem' }}><strong>Datenerfassung auf unserer Website</strong><br/>Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Die Kontaktdaten k{'\u00F6'}nnen Sie dem Impressum dieser Website entnehmen.</p>
            <p style={{ marginTop: '0.8rem' }}>Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. Hierbei kann es sich z.B. um Daten handeln, die Sie in ein Kontaktformular eingeben. Andere Daten werden automatisch beim Besuch der Website durch unsere IT-Systeme erfasst. Das sind vor allem technische Daten (z.B. Internetbrowser, Betriebssystem oder Uhrzeit des Seitenaufrufs).</p>

            <h4 style={{ color: 'var(--cream)', marginTop: '1.2rem', marginBottom: '0.5rem', fontSize: '0.85rem' }}>2. Datenerfassung auf unserer Website</h4>
            <p><strong>Kontaktformular & Propstack CRM</strong><br/>Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben aus dem Anfrageformular inklusive der von Ihnen dort angegebenen Kontaktdaten (Name, E-Mail, Telefonnummer) zwecks Bearbeitung der Anfrage und f{'\u00FC'}r den Fall von Anschlussfragen bei uns gespeichert. Diese Daten geben wir nicht ohne Ihre Einwilligung weiter. Wir nutzen zur Verwaltung unserer Kunden- und Interessentendaten das CRM-System Propstack. Ihre Daten werden zur ordnungsgem{'\u00E4\u00DF'}en und schnellen Bearbeitung direkt in unser Propstack-System {'\u00FC'}bertragen.</p>
            <p style={{ marginTop: '0.8rem' }}><strong>Google reCAPTCHA</strong><br/>Wir nutzen {'\u201E'}Google reCAPTCHA{'\u201C'} auf unseren Websites. Anbieter ist die Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland ({'\u201E'}Google{'\u201C'}). Hierzu analysiert reCAPTCHA das Verhalten des Websitebesuchers anhand verschiedener Merkmale. Die bei der Analyse erfassten Daten werden an Google weitergeleitet. Die Nutzung von reCAPTCHA erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO. Der Websitebetreiber hat ein berechtigtes Interesse daran, seine Webangebote vor missbr{'\u00E4'}uchlicher automatisierter Aussp{'\u00E4'}hung und vor SPAM zu sch{'\u00FC'}tzen.</p>
            <p style={{ marginTop: '0.8rem' }}><strong>Cookies</strong><br/>Wir verwenden teilweise sogenannte Cookies. Cookies richten auf Ihrem Rechner keinen Schaden an und enthalten keine Viren. Cookies dienen dazu, unser Angebot nutzerfreundlicher, effektiver und sicherer zu machen. Die meisten der von uns verwendeten Cookies sind so genannte {'\u201E'}Session-Cookies{'\u201C'}. Sie werden nach Ende Ihres Besuchs automatisch gel{'\u00F6'}scht. Andere Cookies bleiben auf Ihrem Endger{'\u00E4'}t gespeichert bis Sie diese l{'\u00F6'}schen (z.B. Ihre Cookie-Zustimmung).</p>

            <h4 style={{ color: 'var(--cream)', marginTop: '1.2rem', marginBottom: '0.5rem', fontSize: '0.85rem' }}>3. Ihre Rechte</h4>
            <p>Sie haben jederzeit das Recht unentgeltlich Auskunft {'\u00FC'}ber Herkunft, Empf{'\u00E4'}nger und Zweck Ihrer gespeicherten personenbezogenen Daten zu erhalten. Sie haben au{'\u00DF'}erdem ein Recht, die Berichtigung, Sperrung oder L{'\u00F6'}schung dieser Daten zu verlangen. Hierzu sowie zu weiteren Fragen zum Thema Datenschutz k{'\u00F6'}nnen Sie sich jederzeit unter der im Impressum angegebenen Adresse an uns wenden.</p>
          </div>
        </div>
      </div>

      {/* ═══ BACK TO TOP ═══ */}
      <button className={`back-to-top${scrollProgress > 15 ? ' show' : ''}`} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} aria-label="Zurück nach oben">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
      </button>
    </div>
  );
};

export default TresImmoPage;
